/**
 * NOTE: not intended as a standalone component, should be used with Tooltip.js
 */
import React from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable } from "mobx";
import classNames from "classnames";
import isUndefined from "lodash/isUndefined";
import values from "lodash/values";

const TooltipDirections = {
    TOP: "top",
    BOTTOM: "bottom",
    RIGHT: "right",
    LEFT: "left"
};

const tooltipDirectionValues = values(TooltipDirections);
const defaultHorizontalDirection = TooltipDirections.RIGHT;
const defaultVerticalDirection = TooltipDirections.BOTTOM;
const isDirectionHorizontal = direction => {
    return direction === TooltipDirections.RIGHT || direction === TooltipDirections.LEFT;
};

@observer
class TooltipContent extends React.Component {
    @observable horizontalAdjustment = 0;

    static propTypes = {
        visible: PropTypes.bool,
        direction: PropTypes.oneOf(tooltipDirectionValues),
        secondaryDirection: PropTypes.oneOf(tooltipDirectionValues),
        includeHideButton: PropTypes.bool,
        hide: PropTypes.func,
        className: PropTypes.string,
        style: PropTypes.object  // only use if absolutely necessary
    };

    static defaultProps = {
        direction: defaultHorizontalDirection,
        secondaryDirection: defaultVerticalDirection,
        includeHideButton: false
    };

    constructor(props) {
        super(props);
        this.tooltipRef = React.createRef();
    }

    componentDidUpdate() {
        const tooltipContent = this.tooltipRef.current;
        // make sure we only set horizontalAdjustment once
        if (tooltipContent && this.horizontalAdjustment === 0) {
            if (tooltipContent.getBoundingClientRect().left < 0) {
                this.horizontalAdjustment = Math.abs(tooltipContent.getBoundingClientRect().left);
            } else if (!isUndefined(window) && window.screen && tooltipContent.getBoundingClientRect().right > (window.screen.availWidth || window.screen.width)) {
                this.horizontalAdjustment = Math.abs(tooltipContent.getBoundingClientRect().right - (window.screen.availWidth || window.screen.width));
            }
        }
    }

    getSecondaryDirection(primaryDirection) {
        const isPrimaryHorizontal = isDirectionHorizontal(primaryDirection);
        const useDefault = isUndefined(this.props.secondaryDirection) || isPrimaryHorizontal === isDirectionHorizontal(this.props.secondaryDirection);
        if (useDefault) {
            return isPrimaryHorizontal ? defaultVerticalDirection : defaultHorizontalDirection;
        } else {
            return this.props.secondaryDirection;
        }
    }

    render() {
        const direction = this.props.direction || defaultHorizontalDirection;
        const secondaryDirection = this.getSecondaryDirection(direction);
        const mountClasses = ["tooltip-mount", `primary-${direction}`, `secondary-${secondaryDirection}`];
        // allow for horizontal adjustments to be applied at 50px increments
        const contentClasses = classNames("tooltip-content", this.props.className, {
            "adjusted-50": this.horizontalAdjustment > 0 && this.horizontalAdjustment <= 50,
            "adjusted-100": this.horizontalAdjustment > 50 && this.horizontalAdjustment <= 100,
            "adjusted-150": this.horizontalAdjustment > 100 && this.horizontalAdjustment <= 150,
            "adjusted-200": this.horizontalAdjustment > 150 && this.horizontalAdjustment <= 200,
            "adjusted-250": this.horizontalAdjustment > 200 && this.horizontalAdjustment <= 250,
            "adjusted-300": this.horizontalAdjustment > 250 && this.horizontalAdjustment <= 300
        });
        return (
            <div className={classNames(mountClasses)} style={this.props.style}>
                {this.props.visible &&
                    <div ref={this.tooltipRef} className={contentClasses}>
                        {this.props.children}
                    </div>
                }
            </div>
        );
    }
}

TooltipContent.Directions = TooltipDirections;

export default TooltipContent;
